import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  rehydrateStoreContactPhone,
  useLocalities,
  useTranslation,
  useAuthSession,
} from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { useNewsletterSubscriberPermissions } from '@/hooks/use-newsletter-subscriber-permissions';
import { useStore } from './use-store';
import { ContactMethodsForm, type ContactMethodsFormDefaults } from './contact-methods-form';
import { FulfillmentSettingsForm } from './fulfillment-settings-form';
import { ManualOrderApprovalForm } from './manual-order-approval-form';
import { StoreDetailsForm } from './store-details-form';
import { SocialProfilesForm } from './social-profiles-form';
import { NewsletterCollectionForm } from './newsletter-collection-form';
import { NewsletterSubscriberPanel } from './newsletter-subscriber-panel';

/**
 * F-007 §4.2 — `/dashboard/settings` route for the admin contact-methods
 * form. Mirrors `create-store-route.tsx` for the shell chrome + form
 * plumbing but uses `useStore(storeId)` (§4.4) so the cache-shape
 * contract for `['store', storeId]` stays in one place.
 *
 * ## Deep-link banner (`?fix=contact`)
 *
 * When the publish-store dialog raises `missingContactMethod` it
 * navigates the merchant here with `?fix=contact` appended. Two effects:
 *   1. A Saffron-tone info banner renders above the form telling the
 *      merchant they need to add a contact method before they can
 *      publish.
 *   2. The form's WhatsApp input auto-focuses so the merchant can start
 *      typing immediately.
 *
 * Navigating to `/dashboard/settings` without the query param works
 * cleanly — banner and auto-focus are both no-ops.
 *
 * ## Route-scope state
 *
 * The form component (`ContactMethodsForm`) owns all RHF state plus
 * the submit-level error. This route only owns:
 *   - the store query (via `useStore`)
 *   - the derived default values for the form
 *   - the `?fix=contact` deep-link flag
 */
export function StoreSettingsRoute() {
  const { t, dir } = useTranslation();
  const [searchParams] = useSearchParams();
  const shouldFocusContact = searchParams.get('fix') === 'contact';

  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const storeQuery = useStore(storeId);
  // Canonical social writes use the same owner/admin authority as subscriber management.
  const permissions = useNewsletterSubscriberPermissions(storeId);
  const localitiesQuery = useLocalities(supabase);
  const storeRow = storeQuery.data;

  // Memoised so we don't churn the form's useEffect([defaultValues]) on
  // every route re-render. Stored E.164 values become country/local RHF
  // defaults; the form schema transforms back to normalized E.164 on submit.
  const defaultValues = useMemo<ContactMethodsFormDefaults>(() => {
    const whatsapp = rehydrateStoreContactPhone(storeRow?.whatsapp_number ?? null);
    const phone = rehydrateStoreContactPhone(storeRow?.phone_number ?? null);
    return {
      whatsapp: {
        country: whatsapp.country,
        localNumber: whatsapp.localNumber,
      },
      phone: {
        country: phone.country,
        localNumber: phone.localNumber,
      },
    };
  }, [storeRow?.whatsapp_number, storeRow?.phone_number]);

  return (
    <main
      dir={dir}
      data-testid="store-settings-route"
      className="mx-auto max-w-screen-md px-4 py-8 sm:px-6 sm:py-10"
    >
      {shouldFocusContact ? (
        <div
          role="status"
          data-testid="store-settings-fix-banner"
          className="border-admin-lime bg-admin-lime-light/40 text-foreground mb-6 rounded-xl border border-s-4 p-4 text-start text-sm"
        >
          {t('admin.storeSettings.fixBanner')}
        </div>
      ) : null}

      <h1
        className="font-display text-foreground text-start text-3xl sm:text-4xl"
        data-testid="store-settings-title"
      >
        {t('admin.storeSettings.title')}
      </h1>
      <p className="text-muted-foreground mt-2 text-start">{t('admin.storeSettings.subtitle')}</p>

      {storeRow && storeId ? (
        <>
          <StoreDetailsForm
            store={storeRow}
            localities={localitiesQuery.data ?? []}
            localitiesLoading={localitiesQuery.isLoading}
            localitiesError={localitiesQuery.isError}
          />
          <FulfillmentSettingsForm key={`fulfillment:${storeId}`} storeId={storeId} />
          <ManualOrderApprovalForm store={storeRow} />
          <ContactMethodsForm
            storeId={storeId}
            defaultValues={defaultValues}
            autoFocusWhatsapp={shouldFocusContact}
          />
          <SocialProfilesForm
            key={`${storeId}:${authSession.user?.id ?? ''}`}
            client={supabase}
            storeId={storeId}
            enabled={
              permissions.data?.canManageNewsletterSubscribers === true && !permissions.isError
            }
            permissionsLoading={permissions.isLoading}
          />
          <NewsletterCollectionForm key={storeId} store={storeRow} />
          <NewsletterSubscriberPanel key={`subscribers:${storeId}`} storeId={storeId} />
        </>
      ) : storeQuery.isLoading ? (
        <p className="text-muted-foreground mt-8" data-testid="store-settings-loading">
          {t('common.loading')}
        </p>
      ) : (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/10 text-destructive mt-8 rounded-lg border p-4 text-start text-sm"
          data-testid="store-settings-error"
        >
          {t(storeQuery.isError ? 'common.errors.networkFailed' : 'common.errors.storeNotFound')}
        </div>
      )}
    </main>
  );
}
